import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, Trash2, Search as SearchIcon } from 'lucide-react';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import '../AllCss/SavedSearches.css';

function SavedSearches() {
  const toast = useToast();
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const navigate = useNavigate();
  const cars = useSelector((state) => state.cars?.items || []);

  useEffect(() => {
    fetchSavedSearches();
  }, []);

  const fetchSavedSearches = async () => {
    try {
      setLoading(true);
      const response = await api.get('/saved-searches');
      if (response.data.success) {
        setSavedSearches(response.data.data.savedSearches || []);
      }
    } catch (err) {
      console.error('Error fetching saved searches:', err);
      setError('Failed to load saved searches. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getMatchCount = (savedSearch) => {
    // Build filter criteria from saved search
    const matchingCars = cars.filter(car => {
      if (car.status !== 'available') return false;
      
      if (savedSearch.selectedMakes && savedSearch.selectedMakes.length > 0) {
        if (!savedSearch.selectedMakes.includes(car.make)) return false;
      }
      
      if (savedSearch.priceMin && car.price < parseFloat(savedSearch.priceMin)) return false;
      if (savedSearch.priceMax && car.price > parseFloat(savedSearch.priceMax)) return false;
      
      if (savedSearch.yearMin && car.year < parseInt(savedSearch.yearMin)) return false;
      if (savedSearch.yearMax && car.year > parseInt(savedSearch.yearMax)) return false;
      
      if (savedSearch.selectedBodyTypes && savedSearch.selectedBodyTypes.length > 0) {
        if (!savedSearch.selectedBodyTypes.includes(car.bodyType)) return false;
      }
      
      return true;
    });
    
    return matchingCars.length;
  };

  const getFilterTags = (savedSearch) => {
    const tags = [];
    
    // Add makes (brands)
    if (savedSearch.selectedMakes && savedSearch.selectedMakes.length > 0) {
      savedSearch.selectedMakes.forEach(make => tags.push(make));
    }
    
    // Add price range
    if (savedSearch.priceMax) {
      tags.push(`Under $${Number(savedSearch.priceMax).toLocaleString()}`);
    } else if (savedSearch.priceMin) {
      tags.push(`Over $${Number(savedSearch.priceMin).toLocaleString()}`);
    } else if (savedSearch.priceMin && savedSearch.priceMax) {
      tags.push(`$${Number(savedSearch.priceMin).toLocaleString()}-$${Number(savedSearch.priceMax).toLocaleString()}`);
    }
    
    // Add year range
    if (savedSearch.yearMin && savedSearch.yearMax) {
      tags.push(`${savedSearch.yearMin}-${savedSearch.yearMax}`);
    } else if (savedSearch.yearMin) {
      tags.push(`${savedSearch.yearMin}+`);
    } else if (savedSearch.yearMax) {
      tags.push(`Up to ${savedSearch.yearMax}`);
    }
    
    // Add mileage
    if (savedSearch.mileageMax) {
      const mileageValue = Number(savedSearch.mileageMax);
      if (mileageValue >= 1000) {
        tags.push(`Under ${(mileageValue / 1000).toFixed(0)}K miles`);
      } else {
        tags.push(`Under ${mileageValue.toLocaleString()} miles`);
      }
    } else if (savedSearch.mileageMin) {
      tags.push(`Over ${Number(savedSearch.mileageMin).toLocaleString()} miles`);
    }
    
    // Add body types
    if (savedSearch.selectedBodyTypes && savedSearch.selectedBodyTypes.length > 0) {
      savedSearch.selectedBodyTypes.forEach(bodyType => {
        const capitalized = bodyType.charAt(0).toUpperCase() + bodyType.slice(1);
        tags.push(capitalized);
      });
    }
    
    // Add fuel types
    if (savedSearch.selectedFuels && savedSearch.selectedFuels.length > 0) {
      savedSearch.selectedFuels.forEach(fuel => {
        const fuelMap = {
          'gasoline': 'Gas',
          'diesel': 'Diesel',
          'electric': 'Electric',
          'hybrid': 'Hybrid',
          'plug-in hybrid': 'PHEV'
        };
        tags.push(fuelMap[fuel] || fuel);
      });
    }
    
    // Add drivetrain
    if (savedSearch.selectedDrivetrains && savedSearch.selectedDrivetrains.length > 0) {
      savedSearch.selectedDrivetrains.forEach(drivetrain => {
        const drivetrainMap = {
          'fwd': 'FWD',
          'rwd': 'RWD',
          'awd': 'AWD',
          '4wd': '4WD'
        };
        tags.push(drivetrainMap[drivetrain.toLowerCase()] || drivetrain);
      });
    }
    
    // Add exterior colors (limit to 2 most common)
    if (savedSearch.selectedExteriorColors && savedSearch.selectedExteriorColors.length > 0) {
      savedSearch.selectedExteriorColors.slice(0, 2).forEach(color => {
        tags.push(color);
      });
    }
    
    // Add search keywords if available
    if (savedSearch.search && savedSearch.search.trim()) {
      const keywords = savedSearch.search.trim().split(/\s+/).slice(0, 2);
      keywords.forEach(keyword => {
        if (keyword.length > 2) {
          tags.push(keyword);
        }
      });
    }
    
    return tags;
  };

  const getEmailAlertStatus = (savedSearch) => {
    if (!savedSearch.emailAlertsEnabled) {
      return { text: 'No email alerts', icon: <BellOff size={16} /> };
    }
    // You can add logic here to determine instant vs daily based on savedSearch settings
    return { text: 'Instant email alerts', icon: <Bell size={16} /> };
  };

  const handleSeeMatches = (savedSearch) => {
    // Build query params from saved search
    const params = new URLSearchParams();
    
    if (savedSearch.selectedMakes && savedSearch.selectedMakes.length > 0) {
      params.set('makes', savedSearch.selectedMakes.join(','));
    }
    if (savedSearch.priceMin) params.set('priceMin', savedSearch.priceMin);
    if (savedSearch.priceMax) params.set('priceMax', savedSearch.priceMax);
    if (savedSearch.yearMin) params.set('yearMin', savedSearch.yearMin);
    if (savedSearch.yearMax) params.set('yearMax', savedSearch.yearMax);
    if (savedSearch.selectedBodyTypes && savedSearch.selectedBodyTypes.length > 0) {
      params.set('bodyTypes', savedSearch.selectedBodyTypes.join(','));
    }
    
    navigate(`/inventory?${params.toString()}`);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/saved-searches/${id}`);
      setSavedSearches(savedSearches.filter(search => search.id !== id));
      setDeleteConfirmId(null);
    } catch (err) {
      console.error('Error deleting saved search:', err);
      toast.error('Failed to delete saved search. Please try again.');
    }
  };

  const handleToggleEmailAlerts = async (savedSearch) => {
    try {
      const updated = await api.put(`/saved-searches/${savedSearch.id}`, {
        ...savedSearch,
        emailAlertsEnabled: !savedSearch.emailAlertsEnabled,
      });
      setSavedSearches(savedSearches.map(search => 
        search.id === savedSearch.id ? updated.data.data.savedSearch : search
      ));
    } catch (err) {
      console.error('Error updating saved search:', err);
      toast.error('Failed to update email alerts. Please try again.');
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="saved-searches-container">
          <div className="saved-searches-loading">Loading saved searches...</div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="saved-searches-container">
          <div className="saved-searches-error">{error}</div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="saved-searches-container">
        <div className="saved-searches-header">
          <h1>Saved searches</h1>
        </div>

        {savedSearches.length === 0 ? (
          <div className="saved-searches-empty">
            <SearchIcon size={48} />
            <h2>No saved searches yet</h2>
            <p>Save your search filters to quickly find matching vehicles later.</p>
            <button 
              className="saved-searches-browse-btn"
              onClick={() => navigate('/inventory')}
            >
              Browse Inventory
            </button>
          </div>
        ) : (
          <div className="saved-searches-list">
            {savedSearches.map((search) => {
              const matchCount = getMatchCount(search);
              const tags = getFilterTags(search);
              const alertStatus = getEmailAlertStatus(search);
              
              return (
                <div key={search.id} className="saved-search-card">
                  <div className="saved-search-content">
                    <div className="saved-search-header">
                      <h2 className="saved-search-title">{search.name}</h2>
                    </div>
                    
                    <div className="saved-search-details">
                      <span className="saved-search-matches">
                        {matchCount.toLocaleString()} {matchCount === 1 ? 'match' : 'matches'}
                      </span>
                      <span className="saved-search-alerts">
                        {alertStatus.icon}
                        {alertStatus.text}
                      </span>
                    </div>
                    
                    {tags.length > 0 && (
                      <div className="saved-search-tags">
                        {tags.map((tag, index) => (
                          <span key={index} className="saved-search-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="saved-search-actions">
                    <button
                      className="saved-search-see-matches-btn"
                      onClick={() => handleSeeMatches(search)}
                    >
                      See matches
                    </button>
                    <button
                      className="saved-search-action-btn"
                      onClick={() => handleToggleEmailAlerts(search)}
                      title={search.emailAlertsEnabled ? 'Disable email alerts' : 'Enable email alerts'}
                    >
                      {alertStatus.icon}
                    </button>
                    <button
                      className="saved-search-action-btn saved-search-delete-btn"
                      onClick={() => setDeleteConfirmId(search.id)}
                      title="Delete search"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  
                  {deleteConfirmId === search.id && (
                    <div className="saved-search-delete-confirm">
                      <p>Are you sure you want to delete this search?</p>
                      <div className="saved-search-delete-actions">
                        <button
                          className="saved-search-delete-cancel"
                          onClick={() => setDeleteConfirmId(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="saved-search-delete-confirm-btn"
                          onClick={() => handleDelete(search.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}

export default SavedSearches;

